import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { ShieldAlert, Loader2, Users, Sparkles, GraduationCap, Crown, Check } from 'lucide-react';

/**
 * Two-step first-login gate for admin-invited users.
 *
 *   Step 1 — "Set your password":   appears when `must_change_password`
 *   Step 2 — "Pick your plan":      appears when `must_choose_tier`
 *
 * Mounted globally in App.js so it works across every route. Blocks all
 * other interaction until the user finishes both steps.
 */

const TIERS = [
  {
    id: 'open_play', name: 'Open Play', price: 'Free', track: 'Player',
    icon: Users, color: '#64748b',
    blurb: 'Browse events, view instructor contacts, post events, and read the Discussion Board. Always free.',
  },
  {
    id: 'verified_player', name: 'Verified Player', price: '$0.99/mo · $9.99/yr', track: 'Player',
    icon: Sparkles, color: '#56dfb4',
    blurb: 'Everything in Open Play, plus: search & connect with players, in-site messaging, event alerts, post Last-Minute Game Requests.',
  },
  {
    id: 'instructor_starter', name: 'Instructor Starter', price: 'Free', track: 'Instructor',
    icon: GraduationCap, color: '#64748b',
    blurb: 'Free instructor membership — get listed in the directory and share your events.',
  },
  {
    id: 'instructor_plus', name: 'Instructor Advantage', price: '$9.99/mo · $99.99/yr', track: 'Instructor',
    icon: Sparkles, color: '#56c6df',
    blurb: 'Everything in Instructor Starter, plus: messaging, alerts, free-to-attend events post free, paid events discounted to $6.99.',
  },
  {
    id: 'instructor_unlimited', name: 'Instructor Unlimited', price: '$29.99/mo · $299.99/yr', track: 'Instructor',
    icon: Crown, color: '#b456df',
    blurb: 'Everything in Instructor Advantage, plus: unlimited free event posting, Premium Instructor badge, priority placement.',
  },
];

export default function MustChangePasswordGate() {
  const { user, refreshUser, logout } = useAuth();

  if (!user || (!user.must_change_password && !user.must_choose_tier)) return null;

  return (
    <div
      className="fixed inset-0 z-[100] bg-slate-900/60 backdrop-blur-md flex items-center justify-center p-4 overflow-y-auto"
      data-testid="must-change-password-gate"
    >
      <div className="bg-white rounded-2xl max-w-2xl w-full shadow-2xl my-8">
        {user.must_change_password ? (
          <PasswordStep refreshUser={refreshUser} logout={logout} />
        ) : (
          <TierStep refreshUser={refreshUser} logout={logout} />
        )}
      </div>
    </div>
  );
}

function PasswordStep({ refreshUser, logout }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (next.length < 6) { toast.error('New password must be at least 6 characters.'); return; }
    if (next !== confirm) { toast.error('Passwords don\'t match.'); return; }
    if (next === current) { toast.error('New password must differ from your temporary one.'); return; }
    setSubmitting(true);
    try {
      await api.post('/profile/change-password', { current_password: current, new_password: next });
      toast.success('Password saved. Now pick the plan that fits you.');
      await refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally { setSubmitting(false); }
  };

  return (
    <>
      <div className="p-6 border-b border-slate-100">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-[#df5681]/15 flex items-center justify-center flex-shrink-0">
            <ShieldAlert className="w-5 h-5 text-[#df5681]" />
          </div>
          <div>
            <p className="text-xs uppercase tracking-wider text-slate-400">Step 1 of 2</p>
            <h2 className="text-lg font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Set Your Password</h2>
          </div>
        </div>
        <p className="text-sm text-slate-500">
          Welcome! You were invited with a temporary password. Pick a new one below.
        </p>
      </div>
      <form className="p-6 space-y-4" onSubmit={submit} autoComplete="off">
        <div>
          <Label htmlFor="mcp-current" className="text-sm mb-1 block">Temporary password *</Label>
          <Input id="mcp-current" type="password" value={current} onChange={e => setCurrent(e.target.value)} className="rounded-xl" data-testid="mcp-current-password" autoFocus />
        </div>
        <div>
          <Label htmlFor="mcp-new" className="text-sm mb-1 block">New password * <span className="text-xs text-slate-400">(at least 6 characters)</span></Label>
          <Input id="mcp-new" type="password" value={next} onChange={e => setNext(e.target.value)} className="rounded-xl" data-testid="mcp-new-password" />
        </div>
        <div>
          <Label htmlFor="mcp-confirm" className="text-sm mb-1 block">Confirm new password *</Label>
          <Input id="mcp-confirm" type="password" value={confirm} onChange={e => setConfirm(e.target.value)} className="rounded-xl" data-testid="mcp-confirm-password" />
        </div>
        <Button type="submit" disabled={submitting} className="w-full rounded-full bg-[#56dfb4] text-slate-900 hover:bg-[#3ec49b] gap-2" data-testid="mcp-submit">
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
          Save and Continue
        </Button>
        <button type="button" onClick={logout} className="block mx-auto text-xs text-slate-400 hover:text-slate-600 underline" data-testid="mcp-logout">
          Log out instead
        </button>
      </form>
    </>
  );
}

function TierStep({ refreshUser, logout }) {
  const navigate = useNavigate();
  const [selected, setSelected] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!selected) { toast.error('Please pick a plan.'); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post('/profile/choose-tier', { tier: selected });
      await refreshUser();
      if (data.redirect) {
        toast.success("Let's set up payment for your plan.");
        navigate(data.redirect);
      } else {
        toast.success("You're all set! Welcome to Meet For Mahjong.");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save plan');
    } finally { setSubmitting(false); }
  };

  return (
    <>
      <div className="p-6 border-b border-slate-100">
        <p className="text-xs uppercase tracking-wider text-slate-400 mb-1">Step 2 of 2</p>
        <h2 className="text-lg font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Pick Your Plan</h2>
        <p className="text-sm text-slate-500 mt-1">
          Choose the membership that fits how you'll use Meet For Mahjong. You can upgrade or downgrade later from Settings.
        </p>
      </div>
      <div className="p-6 space-y-3 max-h-[60vh] overflow-y-auto">
        {TIERS.map(t => {
          const isSelected = selected === t.id;
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setSelected(t.id)}
              data-testid={`mcp-tier-${t.id}`}
              className={`w-full text-left p-4 rounded-xl border-2 transition-all ${isSelected ? 'border-[#56dfb4] bg-[#56dfb4]/5' : 'border-slate-200 hover:border-slate-300'}`}
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${t.color}15` }}>
                  <Icon className="w-5 h-5" style={{ color: t.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div>
                      <h3 className="font-semibold text-[#0f172a]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        {t.name} <span className="text-xs font-normal text-slate-400">· {t.track}</span>
                      </h3>
                      <p className="text-xs font-bold mt-0.5" style={{ color: t.color }}>{t.price}</p>
                    </div>
                    {isSelected && (
                      <span className="w-6 h-6 rounded-full bg-[#56dfb4] flex items-center justify-center flex-shrink-0">
                        <Check className="w-3.5 h-3.5 text-white" />
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mt-1.5 leading-relaxed">{t.blurb}</p>
                </div>
              </div>
            </button>
          );
        })}
      </div>
      <div className="p-6 border-t border-slate-100 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
        <button type="button" onClick={logout} className="text-xs text-slate-400 hover:text-slate-600 underline" data-testid="mcp-tier-logout">
          Log out instead
        </button>
        <Button
          onClick={submit}
          disabled={submitting || !selected}
          className="rounded-full bg-[#56dfb4] text-slate-900 hover:bg-[#3ec49b] gap-2 px-8"
          data-testid="mcp-tier-submit"
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
          {selected && TIERS.find(t => t.id === selected)?.price === 'Free'
            ? 'Continue with this plan'
            : selected
              ? 'Continue to Payment'
              : 'Pick a plan to continue'}
        </Button>
      </div>
    </>
  );
}
